import React, { useState, CSSProperties } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  User, BarChart2, Users, Plus, LogOut, Settings, ChevronDown, ChevronRight, Gauge, Wallet, CalendarCheck, ListChecks, Clock
} from 'lucide-react'
import { projectsApi } from '../../api/projects'
import { useAuthStore } from '../../store/authStore'
import { Avatar } from '../common/Avatar'
import { Modal } from '../common/Modal'
import { Button } from '../common/Button'
import { canEditUsers, canViewReports, formatRoleLabel, hasFullAccess } from '../../lib/roles'
import { ProfileSettingsModal } from './ProfileSettingsModal'

const colors = {
  sidebar: '#1e1e2e',
  sidebarText: '#cdd6f4',
  sidebarActive: '#313244',
  sidebarHover: '#2a2a3e',
}

const navItem = (active: boolean, danger = false): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '7px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13.5px',
  fontWeight: 500,
  color: danger ? '#f38ba8' : active ? '#cdd6f4' : '#a6adc8',
  background: active ? colors.sidebarActive : 'transparent',
  transition: 'background 0.15s, color 0.15s',
  userSelect: 'none',
  border: 'none',
  width: '100%',
  textAlign: 'left',
})

const PRESET_COLORS = ['#6366f1', '#ec4899', '#f97316', '#22c55e', '#14b8a6', '#eab308']

function canAccessAccounting(user: { role?: string; canSubmitExpenses?: boolean } | null | undefined): boolean {
  return !!user?.canSubmitExpenses
}

export const Sidebar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user, logout } = useAuthStore()
  const [projectsExpanded, setProjectsExpanded] = useState(true)
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    color: PRESET_COLORS[0],
    identifier: '',
    slackChannelId: '',
    showOnDashboard: false,
  })
  const [creating, setCreating] = useState(false)

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.listProjects(),
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const projects = projectsData?.data?.data || []
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/')

  const handleNav = (path: string) => navigate(path)

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) return
    setCreating(true)
    try {
      const { projectsApi: pApi } = await import('../../api/projects')
      const created = await pApi.createProject(newProject)
      const createdProject = created.data.data
      queryClient.setQueryData(['projects'], (oldData: typeof projectsData | undefined) => {
        if (!createdProject?.id) return oldData
        if (!oldData?.data?.data) {
          return { data: { data: [createdProject] } }
        }
        const exists = oldData.data.data.some((project) => project.id === createdProject.id)
        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: exists ? oldData.data.data : [...oldData.data.data, createdProject],
          },
        }
      })
      await queryClient.invalidateQueries({ queryKey: ['projects'] })
      await queryClient.invalidateQueries({ queryKey: ['project-values-projects'] })
      await queryClient.refetchQueries({ queryKey: ['projects'], type: 'active' })
      setCreateProjectOpen(false)
      setNewProject({ name: '', description: '', color: PRESET_COLORS[0], identifier: '', slackChannelId: '', showOnDashboard: false })
      if (createdProject?.id) {
        navigate(`/projects/${createdProject.id}`)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setCreating(false)
    }
  }

  const autoIdentifier = (name: string) =>
    name.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 4) || ''

  return (
    <>
      <div
        style={{
          width: '240px',
          minWidth: '240px',
          height: '100%',
          background: colors.sidebar,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRight: '1px solid #2a2a3e',
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: '18px 16px 12px',
            borderBottom: '1px solid #2a2a3e',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: 30,
                height: 30,
                background: '#6366f1',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: '14px',
                color: '#fff',
              }}
            >
              PM
            </div>
            <span style={{ color: '#cdd6f4', fontWeight: 600, fontSize: '15px' }}>PM System</span>
          </div>
        </div>

        {/* Nav */}
        <div style={{ padding: '10px 8px', flexShrink: 0 }}>
          <NavButton
            icon={<User size={15} />}
            label="My Issues"
            active={isActive('/my-issues')}
            onClick={() => handleNav('/my-issues')}
          />
          {canViewReports(user?.role) && (
            <NavButton
              icon={<BarChart2 size={15} />}
              label="Dashboard"
              active={isActive('/manager-home') || isActive('/dashboard') || isActive('/workload')}
              onClick={() => handleNav('/manager-home')}
            />
          )}
          {canViewReports(user?.role) && (
            <NavButton
              icon={<ListChecks size={15} />}
              label="Dashboard 2"
              active={isActive('/dashboard-2')}
              onClick={() => handleNav('/dashboard-2')}
            />
          )}
          {user?.role !== 'CLIENT' && (
            <NavButton
              icon={<CalendarCheck size={15} />}
              label="Leave"
              active={isActive('/leaves')}
              onClick={() => handleNav('/leaves')}
            />
          )}
          {user?.role !== 'CLIENT' && (
            <NavButton
              icon={<Clock size={15} />}
              label="Attendance"
              active={isActive('/attendance')}
              onClick={() => handleNav('/attendance')}
            />
          )}
          {hasFullAccess(user?.role) && (
            <NavButton
              icon={<Gauge size={15} />}
              label="Project Values"
              active={isActive('/project-values')}
              onClick={() => handleNav('/project-values')}
            />
          )}
          {canViewReports(user?.role) && (
            <NavButton
              icon={<Gauge size={15} />}
              label="People"
              active={isActive('/people-performance')}
              onClick={() => handleNav('/people-performance')}
            />
          )}
          {canEditUsers(user?.role) && (
            <NavButton
              icon={<Users size={15} />}
              label="Users"
              active={isActive('/admin/users')}
              onClick={() => handleNav('/admin/users')}
            />
          )}
          {canAccessAccounting(user) && (
            <NavButton
              icon={<Wallet size={15} />}
              label="Accounting"
              active={isActive('/accounting')}
              onClick={() => handleNav('/accounting')}
            />
          )}
        </div>

        {/* Projects */}
        <div style={{ padding: '0 8px', flex: 1, overflowY: 'auto' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '4px 8px 6px',
            }}
          >
            <button
              onClick={() => setProjectsExpanded((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#6c7086',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                padding: 0,
              }}
            >
              {projectsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Projects
            </button>
            {hasFullAccess(user?.role) && (
              <button
                onClick={() => setCreateProjectOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6c7086',
                  display: 'flex',
                  padding: '2px',
                  borderRadius: '4px',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#cdd6f4')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#6c7086')}
              >
                <Plus size={14} />
              </button>
            )}
          </div>

          {projectsExpanded && (
            <div>
              {projects.length === 0 && (
                <p style={{ color: '#585b70', fontSize: '12px', padding: '4px 12px' }}>
                  No projects yet
                </p>
              )}
              {projects.map((project) => {
                const active = location.pathname.startsWith(`/projects/${project.id}`)
                return (
                  <button
                    key={project.id}
                    onClick={() => handleNav(`/projects/${project.id}`)}
                    style={navItem(active)}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.background = colors.sidebarHover
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = 'transparent'
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: project.color,
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {project.name}
                    </span>
                    {active && hasFullAccess(user?.role) && (
                      <Settings
                        size={13}
                        style={{ opacity: 0.6, flexShrink: 0 }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}/settings`) }}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* User section */}
        <div
          style={{
            padding: '12px 8px',
            borderTop: '1px solid #2a2a3e',
            flexShrink: 0,
          }}
        >
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => setProfileOpen(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  flex: 1,
                  padding: 0,
                  textAlign: 'left',
                }}
              >
                <Avatar user={user} size="sm" />
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <p
                    style={{
                      color: '#cdd6f4',
                      fontSize: '13px',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      margin: 0,
                    }}
                  >
                    {user.fullName}
                  </p>
                  <p style={{ color: '#8b93b8', fontSize: '11px', fontWeight: 600, margin: 0 }}>
                    {formatRoleLabel(user.role)}
                  </p>
                </div>
              </button>
              <button
                onClick={logout}
                title="Sign out"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#585b70',
                  display: 'flex',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#f38ba8')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#585b70')}
              >
                <LogOut size={15} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create Project Modal */}
      <Modal isOpen={createProjectOpen} onClose={() => setCreateProjectOpen(false)} title="New Project">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
              Project Name *
            </label>
            <input
              value={newProject.name}
              onChange={(e) => {
                const name = e.target.value
                setNewProject((p) => ({
                  ...p,
                  name,
                  identifier: p.identifier || autoIdentifier(name),
                }))
              }}
              placeholder="e.g. Website Redesign"
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
              Identifier *
            </label>
            <input
              value={newProject.identifier}
              onChange={(e) =>
                setNewProject((p) => ({
                  ...p,
                  identifier: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 6),
                }))
              }
              placeholder="PROJ"
              maxLength={6}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'monospace',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
              Description
            </label>
            <textarea
              value={newProject.description}
              onChange={(e) => setNewProject((p) => ({ ...p, description: e.target.value }))}
              placeholder="Optional description..."
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
              />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
              Slack Channel ID
            </label>
            <input
              value={newProject.slackChannelId}
              onChange={(e) => setNewProject((p) => ({ ...p, slackChannelId: e.target.value }))}
              placeholder="e.g. C08ABC12345"
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              background: '#f8fafc',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={newProject.showOnDashboard}
              onChange={(e) => setNewProject((p) => ({ ...p, showOnDashboard: e.target.checked }))}
              style={{ marginTop: '2px' }}
            />
            <span>
              <span style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>
                Show in Project Values
              </span>
              <span style={{ display: 'block', marginTop: '2px', fontSize: '12px', color: '#64748b', lineHeight: 1.4 }}>
                Include this project on the employee estimate vs actual dashboard.
              </span>
            </span>
          </label>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
              Color
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setNewProject((p) => ({ ...p, color: c }))}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: c,
                    border: newProject.color === c ? '3px solid #1a202c' : '3px solid transparent',
                    cursor: 'pointer',
                    outline: newProject.color === c ? '2px solid ' + c : 'none',
                    outlineOffset: '1px',
                    transition: 'transform 0.1s',
                  }}
                />
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setCreateProjectOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={creating} onClick={handleCreateProject}>
              Create Project
            </Button>
          </div>
        </div>
      </Modal>

      <ProfileSettingsModal isOpen={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  )
}

const NavButton: React.FC<{
  icon: React.ReactNode
  label: string
  active: boolean
  onClick: () => void
}> = ({ icon, label, active, onClick }) => {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...navItem(active),
        background: active ? colors.sidebarActive : hovered ? colors.sidebarHover : 'transparent',
      }}
    >
      {icon}
      {label}
    </button>
  )
}
